import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  TextField,
  TableCell,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  TableContainer,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import VantageListHead from "../../../../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  findCust,
  findCustId,
  formatValue,
  getComparator,
} from "../../../../../../../../component/Methods";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import LocatesNotFound from "../../../../../../Popup/Rebalancer/LocatesNotFound";

const Locates = ({
  tickerTrades,
  setLocatesData,
  tableHeader,
  custodian,
  custodians,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [data, setData] = useState(tickerTrades);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");
  const [openUnmatchedDialog, setOpenUnmatchedDialog] = useState(false);
  const [unmatchedTickers, setUnmatchedTickers] = useState([]);

  useEffect(() => {
    // Identify incorrect tickers
    const incorrectTickers = tickerTrades.filter((trade) => {
      // Define your criteria for incorrect tickers
      return !data.some(
        (d) => d.ticker.toLowerCase() === trade.ticker.toLowerCase()
      );
    });

    // Separate correct tickers
    const correctTickers = tickerTrades.filter((trade) => {
      return data.some(
        (d) => d.ticker.toLowerCase() === trade.ticker.toLowerCase()
      );
    });

    // Sort correct tickers alphabetically
    const sortedCorrectTickers = correctTickers.sort((a, b) => {
      return a.ticker.localeCompare(b.ticker);
    });

    // Combine incorrect tickers at the top followed by sorted correct tickers
    let combinedData = [...incorrectTickers, ...sortedCorrectTickers];


    combinedData = combinedData.filter((trade) => trade.residual > 0);

    const filteredData = applySortFilter(
      combinedData,
      getComparator(order, orderBy)
    );
    setData(filteredData);
  }, [order, orderBy, tickerTrades]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleAvailableChange = (e, index, key, id) => {
    let value = parseInt(e.target.value, 10);

    if (isNaN(value)) {
      value = 0;
    }
    if (value < 0) {
      value = 0;
    }

    const updatedTickerTrades = tickerTrades.map((trade) =>
      trade.key === key && trade.id === id
        ? { ...trade, available: value }
        : trade
    );

    setLocatesData(updatedTickerTrades, custodian);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (action) => {
    if (action === "Copy") {
      // Aggregate share counts for similar tickers before copying
      const aggregatedData = data.reduce((acc, trade) => {
        const existingTrade = acc.find((t) => t.ticker === trade.ticker);
        if (existingTrade) {
          existingTrade.residual += trade.residual;
        } else {
          acc.push({ ...trade });
        }
        return acc;
      }, []);

      const formattedData = aggregatedData
        .map((trade) => `${trade.ticker}\t${trade.residual}`)
        .join("\n");
      navigator.clipboard
        .writeText(formattedData)
        .then(() => {
          toast.success("Data copied to clipboard successfully!");
        })
        .catch((error) => {
          toast.error("Failed to copy data to clipboard");
        });
    } else if (action === "Paste") {
      navigator.clipboard
        .readText()
        .then((clipboardData) => {
          try {
            const pastedData = clipboardData
              ?.split("\n")
              ?.map((line) => {
                const cleanedLine = line?.trim();
                if (cleanedLine === "") {
                  return null;
                }

                const parts =
                  custodian === "4b948dac7f5dcfe0f93d73525f2533f5"
                    ? cleanedLine?.split("\t")
                    : cleanedLine?.split(/\s+/);

                if (custodian === "4b948dac7f5dcfe0f93d73525f2533f5") {
                  if (parts.length < 4) {
                    throw new Error(
                      "Invalid format. Each line should contain at least 4 tab-separated values."
                    );
                  }
                  const [ticker, , , available] = parts;
                  const parsedAvailable = available === "--" ? 0 : available;
                  if (isNaN(parsedAvailable)) {
                    throw new Error(
                      `Invalid number format for ticker "${ticker}". Available shares must be a number.`
                    );
                  }
                  return { ticker, available: parseInt(parsedAvailable, 10) };
                } else {
                  if (parts.length !== 2) {
                    throw new Error(
                      "Invalid format. Each line should contain ticker and available shares."
                    );
                  }
                  const [ticker, available] = parts;
                  if (isNaN(available)) {
                    throw new Error(
                      `Invalid number format for ticker "${ticker}". Available shares must be a number.`
                    );
                  }
                  return { ticker, available: parseInt(available, 10) };
                }
              })
              .filter(Boolean);

            if (pastedData.length === 0) {
              throw new Error(
                "No valid data found in clipboard. Please check the format."
              );
            }

            const updatedTickerTrades = tickerTrades.map((trade) => {
              const matchingData = pastedData.find(
                (item) =>
                  item?.ticker?.toLowerCase() === trade?.ticker?.toLowerCase()
              );
              if (matchingData) {
                const totalRequired = tickerTrades
                  .filter((t) => t.ticker === trade.ticker)
                  .reduce((sum, t) => sum + t.residual, 0);

                if (matchingData.available === 0) {
                  return { ...trade, available: 0 };
                } else if (matchingData.available >= totalRequired) {
                  return { ...trade, available: trade.residual };
                } else {
                  const ratio = matchingData.available / totalRequired;
                  return {
                    ...trade,
                    available: Math.floor(trade.residual * ratio),
                  };
                }
              }
              return trade;
            });

            const unmatchedTickersList = pastedData.filter(
              (pastedItem) =>
                !tickerTrades.some(
                  (trade) =>
                    trade?.ticker?.toLowerCase() ===
                    pastedItem?.ticker?.toLowerCase()
                )
            );

            if (unmatchedTickersList.length > 0) {
              setUnmatchedTickers(unmatchedTickersList);
              setOpenUnmatchedDialog(true);
            }

            if (updatedTickerTrades.length === 0) {
              toast.warning("No matching tickers found in the current data.");
              return;
            }

            setLocatesData(updatedTickerTrades, custodian);
            toast.success("Data pasted successfully!");
          } catch (error) {
            toast.error(error.message);
            console.error("Error parsing pasted data:", error);
          }
        })
        .catch((error) => {
          toast.error("Failed to read clipboard data. Please try again.");
          console.error("Error reading clipboard data:", error);
        });
    }
    handleMenuClose();
  };

  return (
    <>
      <LocatesNotFound
        open={openUnmatchedDialog}
        onClose={() => setOpenUnmatchedDialog(false)}
        unmatchedTickers={unmatchedTickers}
      />

      <Box
        sx={{
          // border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "2px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "10px",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold !important",
              fontSize: "15px !important",
            }}
          >
            Custodian: {findCust(custodian, custodians)} <br/> Locates Count: {new Set(data.map(item => item.ticker)).size}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                marginRight: "10px",
                cursor: "pointer",
                color: theme.palette.primary.main,
              }}
              onClick={() => handleMenuItemClick("Copy")}
            >
              Copy
            </Typography>
            <Typography
              sx={{
                marginRight: "10px",
                cursor: "pointer",
                color: theme.palette.primary.main,
              }}
              onClick={() => handleMenuItemClick("Paste", custodian)}
            >
              Paste
            </Typography>
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
          </Box>
          {/* Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleMenuItemClick("Text")} disabled>
              Fetch via API
            </MenuItem>
          </Menu>
        </Box>

        <TableContainer style={{ height: "630px" }}>
          <Table
            size="small"
            stickyHeader
            aria-label="a dense table"
            sx={{
              height: "max-content",
            }}
          >
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={tableHeader}
              noStatus={true}
              customizeFont={12}
              rowCount={data.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data.length == 0 ? (
                <TableRow>
                  <TableCell colSpan={tableHeader.length} align="center">
                    No locates required
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              )}
              {data.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    overflow: "auto",
                    backgroundColor: theme.palette.background.paper,
                    "&:hover": {
                      background: theme.palette.action.hover,
                      cursor: "pointer",
                    },
                    "& > .MuiTableCell-root": {
                      paddingTop: "0px",
                      paddingBottom: "0px !important",
                      lineHeight: "1.2",
                      "& .MuiInputBase-input": {
                        padding: "0px 0px !important",
                      },
                      "& .MuiIconButton-root": {
                        padding: "0px",
                      },
                    },
                  }}
                >
                  <Tablecell 
                    align="left"
                    sx={{
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    {row?.ticker}
                  </Tablecell>
                  <Tablecell 
                    align="right"
                    sx={{
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    {formatValue(row?.residual, true)}
                  </Tablecell>
                  <TableCell
                    align="right"
                    sx={{ 
                      padding: "5px",
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    <TextField
                      value={row.available}
                      onChange={(e) =>
                        handleAvailableChange(e, index, row?.key, row?.id)
                      }
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: {
                          fontSize: "12px",
                          fontWeight: "500",
                          width: "10ch",
                          marginRight: "0.5ch",
                          paddingTop: "0px",
                          paddingBottom: "0px",
                          color: theme.palette.text.primary,
                        },
                      }}
                      inputProps={{
                        type: "number",
                        style: {
                          textAlign: "right",
                          paddingTop: "0px",
                          paddingBottom: "0px",
                          color: theme.palette.text.primary,
                          MozAppearance: "textfield", // For Firefox
                          // For hiding the spin buttons specifically in WebKit browsers
                          "&::WebkitOuterSpinButton": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          "&::WebkitInnerSpinButton": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        },
                      }}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (["e", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onBlur={(e) => {
                        let value = parseInt(e.target.value, 10);
                        if (isNaN(value) || value < 0) {
                          e.target.value = 0;
                          handleAvailableChange(
                            { target: { value: 0 } },
                            index,
                            row?.key,
                            row?.id
                          );
                        } else {
                          handleAvailableChange(e, index, row?.key, row?.id);
                        }
                      }}
                    />
                  </TableCell>
                  <Tablecell 
                    align="left"
                    sx={{
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    {row?.fund}
                  </Tablecell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default Locates;
